import {
  Badge,
  Button,
  CardMedia,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { RoomContext } from "App/context";
import React, { PropsWithChildren, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import Typography from "../../../common/components/Typography";
import { Link, useLocation } from "react-router-dom";
import Avatar from "common/components/Avatar";
import AvatarSelector from "../AvatarSelector";
import logo from "assets/images/logo.png";
import twitch from "assets/images/twitch.png";
import youtube from "assets/images/youtube.png";
import twitter from "assets/images/twitter.png";
import discord from "assets/images/discord.png";

declare const process: {
  env: {
    REACT_APP_HOME_URL?: string;
  };
};

type NavbarProps = {};

const ImgLink = (props: {
  link: string;
  image: string;
  newTab?: boolean;
  size?: "small" | "big";
}) => {
  const { link, image, newTab = false, size = "small" } = props;
  return (
    <Link to={link} target={newTab ? "_blank" : undefined}>
      <CardMedia
        component="img"
        src={image}
        sx={{
          height: size === "small" ? "1.5rem" : "3rem",
          width: size === "small" ? "1.5rem" : "3rem",
        }}
      />
    </Link>
  );
};
const LoadingLinkWrapper = (
  props: PropsWithChildren<{ loading: boolean; link: string }>,
) =>
  props.loading ? (
    <>{props.children}</>
  ) : (
    <Link to={props.link} style={{ textDecoration: "none" }}>
      {props.children}
    </Link>
  );
const TabLink = (props: { loading: boolean; link: string; label: string }) => {
  const { loading, link, label } = props;
  const { pathname } = useLocation();

  return (
    <LoadingLinkWrapper loading={loading} link={link}>
      <Typography
        sx={{
          color: ({ palette }) =>
            (link === "/" && pathname === link) ||
            (link !== "/" && pathname.includes(link))
              ? palette.primary.main
              : "white",
        }}
        textAlign="center"
        size="sm"
      >
        {label}
      </Typography>
    </LoadingLinkWrapper>
  );
};

const Navbar = (props: NavbarProps) => {
  const { t } = useTranslation();
  const { localPlayer } = useContext(RoomContext);
  const [openAS, setOpenAS] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const theme = useTheme();
  const isMobileMenu = useMediaQuery(theme.breakpoints.down("md"));

  const loading = localPlayer === undefined;

  const socialLinks = [
    {
      link: process.env.REACT_APP_HOME_URL ?? "",
      image: logo,
      size: "big" as const,
    },
    {
      link: "https://www.twitch.tv/aspig",
      image: twitch,
    },
    {
      link: "https://www.youtube.com/@AsPigTv",
      image: youtube,
    },
    {
      link: "https://twitter.com/AsPigTV",
      image: twitter,
    },
    {
      link: "https://discord.com/invite/9nKbxnzRep",
      image: discord,
    },
  ];

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      gap={1}
    >
      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
        {socialLinks.map((item, index) => (
          <ImgLink
            key={index}
            link={item.link}
            image={item.image}
            newTab={item.size !== undefined}
            size={item.size}
          />
        ))}
      </Stack>

      <Stack direction="row" alignItems="center" gap={2}>
        {isMobileMenu ? (
          <>
            <IconButton
              onClick={handleOpenMenu}
              size="small"
              sx={{
                color: "white",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleCloseMenu}
              PaperProps={{
                sx: { minWidth: "12rem", bgcolor: "background.paper" },
              }}
            >
              <MenuItem onClick={handleCloseMenu} component={Link} to="/">
                <Typography size="sm">{t("Menu")}</Typography>
              </MenuItem>
              <MenuItem onClick={handleCloseMenu} component={Link} to="/box">
                <Badge
                  color="error"
                  invisible={
                    loading ||
                    (localPlayer?.characters !== undefined &&
                      localPlayer?.characters.length > 0)
                  }
                  badgeContent=""
                >
                  <Typography size="sm">{t("Ma box")}</Typography>
                </Badge>
              </MenuItem>
              <MenuItem
                onClick={handleCloseMenu}
                component={Link}
                to="/playersBox"
              >
                <Typography size="sm">{t("Box des joueurs")}</Typography>
              </MenuItem>
              <MenuItem
                onClick={handleCloseMenu}
                component={Link}
                to="/balancing"
              >
                <Typography size="sm">{t("Équilibrage")}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleCloseMenu();
                  setOpenAS(true);
                }}
              >
                <Typography size="sm">{t("Profil")}</Typography>
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            <TabLink loading={loading} link="/" label={t("Menu")} />
            <Divider orientation="vertical" flexItem />
            <Badge
              color="error"
              invisible={
                loading ||
                (localPlayer?.characters !== undefined &&
                  localPlayer?.characters.length > 0)
              }
              badgeContent=""
            >
              <TabLink loading={loading} link="/box" label={t("Ma box")} />
            </Badge>
            <Divider orientation="vertical" flexItem />
            <TabLink
              loading={loading}
              link="/playersBox"
              label={t("Box des joueurs")}
            />
            <Divider orientation="vertical" flexItem />
            <TabLink
              loading={loading}
              link="/balancing"
              label={t("Équilibrage")}
            />
          </>
        )}

        <Button
          onClick={() => setOpenAS(true)}
          sx={{ padding: 0, minWidth: 0 }}
          disabled={loading}
        >
          <Badge
            color="error"
            invisible={
              loading ||
              (localPlayer?.avatar !== undefined &&
                localPlayer.pseudo !== undefined)
            }
            badgeContent=" "
          >
            <Avatar character={localPlayer?.avatar} loading={loading} />
          </Badge>
        </Button>
        <AvatarSelector open={openAS} onClose={() => setOpenAS(false)} />
      </Stack>
    </Stack>
  );
};

export default Navbar;
